import { BaseTag } from '@ViewModels';
import { css } from 'aphrodite';
import { FC, useMemo, useRef, useState } from 'react';
import { Waypoint } from 'react-waypoint';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { Popover, PopoverAnchor, PopoverContent } from '../../components-v2/Popover';
import { TextInput } from '../../components/TextInput';
import { bs } from '../../styles/styles';
import { toSentenceCase } from '../../utils/toSentenceCase';
import { Column, Row } from '../PageLayout/Layout';
import { MultiColorPillProps, Pill, SolidColorPillProps } from '../Pill/Pill';
import { styleSheet } from './styles';

export interface PillListBuilderProps {
	displaySelected?: boolean;
	fetchMoreOptions?: () => void;
	hasMoreOptions?: boolean;
	isLoading?: boolean;
	multiSelect?: boolean;
	onEditOptions?: (options: BaseTag) => void;
	onOptionClick?: (options: BaseTag[]) => void;
	options: BaseTag[];
	placeholder?: string;
	selectedOptions?: BaseTag[];
	setSearchTerm?: (term: string) => void;
	variant: SolidColorPillProps['variant'] | MultiColorPillProps['variant'];
}

export const PillListBuilder: FC<PillListBuilderProps> = ({
	displaySelected,
	fetchMoreOptions,
	hasMoreOptions = false,
	isLoading = false,
	multiSelect = true,
	onEditOptions,
	onOptionClick,
	options,
	placeholder,
	selectedOptions = [],
	setSearchTerm,
	variant,
}) => {
	const [open, setOpen] = useState(false);
	const [inputValue, setInputValue] = useState('');
	const inputRef = useRef<HTMLInputElement>(null);

	const handleClose = (event?: any) => {
		if (!inputRef.current?.contains(event?.target)) {
			setInputValue('');
			setOpen(false);
		}
	};

	const handleAdd = (value: BaseTag) => {
		const values = selectedOptions?.length > 0 ? [...selectedOptions, value] : [value];
		onOptionClick?.(values);

		if (!multiSelect) {
			setOpen(false);
		}
	};

	const handleRemove = (value: BaseTag) => {
		const values = selectedOptions.filter(item => item.name !== value.name);
		onOptionClick?.(values);
	};

	const handleEdit = () => {
		if (inputValue) {
			const newTag: BaseTag = {
				id: '',
				name: inputValue,
			};
			onEditOptions?.(newTag);
			setInputValue('');
			setOpen(false);
		}
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setInputValue(e.target.value);
		setSearchTerm?.(e.target.value);
	};

	const handleInputFocus = () => {
		setOpen(true);
	};

	const filteredOptions = useMemo(() => {
		if (options) {
			const filtered = options.filter(
				(option: BaseTag) => !selectedOptions.find(selectedOption => selectedOption.id === option.id)
			);
			if (!inputValue) {
				return filtered;
			}
			return filtered.filter((tag: BaseTag) => {
				return !inputValue || tag?.name?.toLowerCase().includes(inputValue.toLowerCase());
			});
		}
		return [];
	}, [selectedOptions, inputValue, options]);

	return (
		<Column styles={[styleSheet.wrapper]}>
			{displaySelected &&
				multiSelect &&
				selectedOptions?.length > 0 &&
				selectedOptions.map(option => (
					<Pill
						key={option.id}
						variant={variant}
						label={option.name}
						color={option.color}
						onRemove={() => handleRemove(option)}
					/>
				))}
			<Row styles={[styleSheet.inputRow]}>
				<Row styles={[styleSheet.container]}>
					{!multiSelect && selectedOptions.length > 0 && (
						<Pill
							variant={variant}
							label={selectedOptions?.[0]?.name}
							color={selectedOptions?.[0]?.color}
							onRemove={() => handleRemove(selectedOptions?.[0])}
						/>
					)}
					<Popover open={open}>
						<PopoverAnchor>
							<Row styles={[styleSheet.inputRow]}>
								<TextInput
									type='text'
									value={inputValue}
									onChange={handleInputChange}
									onFocus={handleInputFocus}
									placeholder={placeholder}
									inputRef={inputRef}
									className={css(styleSheet.textInput)}
								/>
							</Row>
						</PopoverAnchor>
						<PopoverContent
							className={css(styleSheet.popoverContent)}
							onEscapeKeyDown={handleClose}
							onPointerDownOutside={handleClose}
							align='start'
							alignOffset={-8}
						>
							{isLoading && inputValue ? (
								<LoadingSpinner />
							) : (
								(filteredOptions.length > 0 || (inputValue && onEditOptions)) && (
									<>
										<Row styles={[bs.flexWrap, bs.gap2]}>
											{filteredOptions.length > 0 &&
												filteredOptions.map(option => (
													<Pill
														variant={variant}
														color={option?.color}
														label={option?.name || ''}
														key={option?.id}
														onClick={() => handleAdd(option)}
													/>
												))}
											{filteredOptions.length === 0 && inputValue && onEditOptions && (
												<Pill
													variant={variant}
													color='gray'
													label={`Add New ${toSentenceCase(variant)}: ${inputValue}`}
													onClick={handleEdit}
												/>
											)}
										</Row>
										{hasMoreOptions && <Waypoint bottomOffset='-200px' onEnter={fetchMoreOptions} />}
									</>
								)
							)}
						</PopoverContent>
					</Popover>
				</Row>
			</Row>
		</Column>
	);
};
